import React, { ReactNode } from 'react';

import { Platform, StyleSheet, View, ViewStyle } from 'react-native';

import { primitive } from 'styles/constants';

export const topStackedCardShadow =
  Platform.OS === 'web' || Platform.OS === 'android'
    ? {
        boxShadow: '1px -1px 2px 1px rgba(0,0,0,0.2)',
      }
    : {
        // iOS
        shadowColor: '#000000',
        shadowOffset: { width: 1, height: -1 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
      };

export const bottomStackedCardShadow =
  Platform.OS === 'web' || Platform.OS === 'android'
    ? {
        boxShadow: '1px -1px 2px 1px rgba(0,0,0,0.1)',
      }
    : {
        // iOS
        shadowColor: '#000000',
        shadowOffset: { width: 1, height: -1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      };

const BottomStackedCard = ({
  isActive,
  isPressed,
  isFocused,
}: {
  isActive?: boolean;
  isPressed?: boolean;
  isFocused?: boolean;
}) => {
  return (
    <View
      pointerEvents="none"
      style={[
        styles.bottomCard,
        isActive ? styles.bgDisabled : styles.bgWhite,
        bottomStackedCardShadow as ViewStyle,
      ]}
    >
      <View
        style={[styles.fullSize, isPressed || isFocused ? styles.bgPressed : styles.bgTransparent]}
      />
    </View>
  );
};

export const StackedCardContainer = ({
  children,
  isActive,
  isPressed,
  isFocused,
  padding,
}: {
  children: ReactNode;
  isActive?: boolean;
  isPressed?: boolean;
  isFocused?: boolean;
  padding?: string[];
}) => {
  // Padding mapping (approximate conversion from theme tokens to pixels)
  // $0 -> 0, $3 -> 12, $4 -> 16
  const resolvePadding = (p?: string[]) => {
    if (!p) {
      return {};
    }
    const isZero = p[0] === '$0';
    if (isZero) {
      return { padding: 0 };
    }
    return { paddingVertical: 12, paddingHorizontal: 16 };
  };

  const paddingStyle = resolvePadding(padding);

  return (
    <>
      <BottomStackedCard isActive={isActive} isPressed={isPressed} isFocused={isFocused} />
      <View
        style={[
          styles.topCard,
          isActive ? styles.bgDisabled : styles.bgWhite,
          topStackedCardShadow as ViewStyle,
        ]}
      >
        <View
          style={[
            styles.innerContent,
            paddingStyle,
            isPressed || isFocused ? styles.bgPressed : styles.bgTransparent,
          ]}
        >
          {children}
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  fullSize: {
    width: '100%',
    height: '100%',
  },
  bottomCard: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 4,
    borderRadius: 10,
    zIndex: 0,
    marginLeft: 4, // $1
  },
  topCard: {
    borderRadius: 10,
    borderWidth: 0,
    borderColor: 'transparent',
    marginLeft: 10,
    marginBottom: 6,
    transform: [{ translateY: 6 }, { translateX: -6 }],
  },
  innerContent: {
    paddingTop: 12,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  bgWhite: {
    backgroundColor: primitive.$white,
  },
  bgDisabled: {
    backgroundColor: primitive.$disabledNoOpacity,
  },
  bgPressed: {
    backgroundColor: '#F5F5F5', // token.background-focus approx
  },
  bgTransparent: {
    backgroundColor: 'transparent',
  },
});
